export type ThemeChoice = 'light' | 'dark' | 'system'
/** Which client surface is running: shapes layout, type scale and theming. */
export type Surface = 'wall' | 'desk' | 'phone'

const KEY = 'office.theme'

export function readTheme(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'dark' || v === 'light' || v === 'system' ? v : 'system'
  } catch { return 'system' }
}

export function applyTheme(choice: ThemeChoice, surface: Surface): void {
  const root = document.documentElement
  root.dataset.surface = surface
  // The wall tablet is pinned to light regardless of system preference: it lives
  // in a bright office, and flipping it dark at night would make the morning
  // transition jarring. It dims instead (docs/03).
  if (surface === 'wall') { root.dataset.theme = 'light'; return }
  if (choice === 'system') delete root.dataset.theme
  else root.dataset.theme = choice
}

export function saveTheme(choice: ThemeChoice): void {
  try { localStorage.setItem(KEY, choice) } catch { /* private mode */ }
}

/**
 * Ambient dimming for the always-on wall display.
 *
 * This is a PANEL BACKLIGHT level, applied natively through the shell's
 * `setBrightness` bridge — it is NOT a CSS filter. Filtering the page greys out
 * pure white and drains the colour out of a design built to be light and blue,
 * which is exactly the wrong trade: dimming the backlight makes the room darker,
 * dimming the pixels makes the design uglier.
 *
 * The board still never inverts to a dark theme; it just gets quieter at night
 * so the morning transition is not jarring (docs/03).
 */
export function ambientDim(hourLocal: number): number {
  if (hourLocal >= 7 && hourLocal < 19) return 1
  if (hourLocal >= 19 && hourLocal < 22) return 0.75
  if (hourLocal >= 22 || hourLocal < 6) return 0.45
  return 0.7
}

/** Push the backlight level to the native shell, if there is one. */
export function applyAmbientBrightness(hourLocal: number): void {
  const shell = (globalThis as { OfficeOS?: { setBrightness?: (f: number) => void } }).OfficeOS
  shell?.setBrightness?.(ambientDim(hourLocal))
}

export function burnInOffset(nowMs: number): { x: number; y: number } {
  // A slow 4-step cycle, one pixel each way, every 7 minutes.
  const step = Math.floor(nowMs / (7 * 60_000)) % 4
  return [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }][step]!
}
